'use client';

import { useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import classNames from 'classnames';
import PageType from '@alltrails/amplitude/enums/PageType';
import useLogWebSectionViewed from '@alltrails/layout/hooks/useLogWebSectionViewed';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import LinkAccordian from '@alltrails/seo/components/LinkAccordian';
import { LocationDetailsResponseType, PageFilterType, SeoLinksSectionKey, SeoLinks as SeoLinksType } from '../../types/locationDetailsPages/shared';
import { getLocationTitleWithFilter, getCityTitleWithFilter } from './seoLinksHelpers';
import { useSectionHeading } from './hooks/useSectionHeading';
import DesktopLinkSection from './DesktopLinkSection';
import styles from './SeoLinks.module.scss';

type LinkSectionProps = {
  links: { label: string; href: string }[];
  title: string;
  isMobileExpanded?: boolean;
};

const LinkSection = ({ links, title, isMobileExpanded = false }: LinkSectionProps) => {
  const [isOpen, setIsOpen] = useState(isMobileExpanded);
  const isMobile = useIsMobileSizedScreen();

  return isMobile ? (
    <LinkAccordian title={title} isOpen={isOpen} toggleIsOpen={() => setIsOpen(!isOpen)} links={links} />
  ) : (
    <div className={styles.desktopLinkSection}>
      <DesktopLinkSection title={title} links={links} />
    </div>
  );
};

const getTitleMessage = (pageFilterUid, locationName, isCityPage, isStatePage, intl) => {
  if (isCityPage) {
    return getCityTitleWithFilter({ filter: pageFilterUid, locationName, intl });
  } else if (isStatePage) {
    return getLocationTitleWithFilter({ filter: pageFilterUid, locationName, intl });
  } else {
    return getLocationTitleWithFilter({ locationName, intl });
  }
};

type SeoLinksProps = {
  locationName: string;
  linkData: {
    key: SeoLinksSectionKey;
    data: SeoLinksType;
  }[];
  pageFilterUid?: PageFilterType;
  isBot?: boolean;
  isLastElement: boolean;
  pageType?: PageType;
  locationType?: LocationDetailsResponseType;
};

const SeoLinks = ({ linkData, locationName, pageFilterUid, isBot = false, isLastElement, pageType, locationType }: SeoLinksProps) => {
  const intl = useIntl();
  const isCityPage = locationType === 'city';
  const isStatePage = locationType === 'state';
  const { getSectionHeading } = useSectionHeading(isCityPage, pageFilterUid);
  const ref = useRef<HTMLDivElement>(null);
  useLogWebSectionViewed(ref, 'Link Block', pageType, { threshold: 0.4 });
  const publicBaseUrl = usePublicBaseUrl();

  return (
    <div className={classNames(styles.container, isLastElement && styles.linksPadding)} ref={ref}>
      <div className={styles.content}>
        <Typography component="h2" variant="heading400" className={styles.title}>
          {getTitleMessage(pageFilterUid, locationName, isCityPage, isStatePage, intl)}
        </Typography>
        <div className={styles.linkSection}>
          {linkData.map(section => {
            if (section.data.length === 0) return null;
            const links = section.data.slice(0, 15); // The max number of links we display is 15
            const mappedLinks = links.map(link => ({ label: link.title, href: `${publicBaseUrl}${link.url}` }));
            const heading = getSectionHeading(section.key, links.length);
            return <LinkSection links={mappedLinks} title={heading} key={section.key} isMobileExpanded={isBot} />;
          })}
        </div>
      </div>
    </div>
  );
};

export default SeoLinks;
